// dailyCache — the single, declarative place that says which live store slices
// survive after hours / refresh, so every data tile shows the last trade day
// instead of going blank (the heatmap's resilience, generalized).
//
// How it scales:
//   • A new widget that reads a slice already listed here (rowsBySym, indexData.SPX)
//     gets after-hours resilience for FREE — nothing to wire.
//   • A new widget that introduces a NEW live-only slice: add ONE entry to
//     DAILY_CACHE_SERIES below. No other file changes.
//
// Mechanics: each slice is persisted once per minute (off the hot path, from the
// existing capture tick in App.js) and hydrated on app mount. Keys are date-stamped
// in ET (`<key>_YYYY-MM-DD`) so the newest day with data wins and stale days are pruned.
//
// NOTE: optionsStore.chainSnaps (heatmap / GEX Phase / Key Levels) is the original
// instance of this pattern but is still wired inline in App.js because it also runs an
// API backfill and flips isDataReady. TODO: migrate it here once the registry grows an
// optional `backfill` / `onHydrate` hook, collapsing everything to one mechanism.

import { useOptionsStore } from './optionsStore';
import useWebSocketDataStore from './WebSocketDataStore';
import { etDateKey } from '../Utils/marketHours';

const etDate = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

const dailyKey = (prefix) => `${prefix}_${etDate()}`;

export function hasCurrentTradingDayLiveData(nowMs = Date.now()) {
  const todayKey = etDateKey(nowMs);
  const chainSnaps = useOptionsStore.getState().chainSnaps || [];
  if (chainSnaps.some((snap) => Number.isFinite(snap?.t) && etDateKey(snap.t) === todayKey)) {
    return true;
  }

  const spxBars = useWebSocketDataStore.getState().indexData?.SPX || [];
  if (spxBars.some((bar) => Number.isFinite(bar?.time) && etDateKey(bar.time) === todayKey)) {
    return true;
  }

  const spxAggregation = useWebSocketDataStore.getState().aggregationState?.SPX;
  return Number.isFinite(spxAggregation?.startTime) && etDateKey(spxAggregation.startTime) === todayKey;
}

// Read the newest stored day for a prefix and prune older days. Returns the parsed
// value or null. Never throws — private mode / corrupt entries must not break boot.
export function hydrateNewestDaily(prefix) {
  try {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith(`${prefix}_`));
    const newest = keys.sort().pop(); // ISO date suffix → lexicographic = chronological
    let parsed = null;
    if (newest) {
      try { parsed = JSON.parse(localStorage.getItem(newest)); } catch { /* corrupt entry */ }
    }
    for (const k of keys) if (k !== newest) localStorage.removeItem(k);
    return parsed;
  } catch { return null; }
}

// The ET date a hydrated value was stored under, or null. Consumers need this to
// tell "restored today's session" from "restored an older one" — the two behave
// differently the moment live data starts arriving.
export function newestDailyDateKey(prefix) {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(`${prefix}_`))
      .sort()
      .pop()
      ?.slice(prefix.length + 1) ?? null;
  } catch { return null; }
}

export function persistDaily(prefix, value) {
  try { localStorage.setItem(dailyKey(prefix), JSON.stringify(value)); }
  catch { /* quota / private mode — non-fatal */ }
}

// The registry. Each entry:
//   key       unique localStorage prefix for the series.
//   read()    return the serializable slice to cache, or null to SKIP this tick
//             (skip = never overwrite a good prior-day cache with empty live state,
//             and never re-stamp RESTORED prior-session data with today's date —
//             that would launder yesterday's chain into a current-session key).
//   write(v, dateKey)  restore a hydrated value, and the ET date it was stored
//             under, into the live store.
export const DAILY_CACHE_SERIES = [
  {
    key: 'chainRows', // optionsStore.rowsBySym (+spot) → chain tables (full / condensed / ATM)
    read: () => {
      const { rowsBySym, spot, rowsTradingDate } = useOptionsStore.getState();
      // Same test the indexBars entry applies to its newest bar: only today's
      // chain may be written under today's key. Asking whether the rows came
      // from a restore missed the tab held open across the close, whose rows are
      // live-written and yesterday's at once.
      if (rowsTradingDate !== etDateKey(Date.now())) return null;
      return rowsBySym && Object.keys(rowsBySym).length ? { rowsBySym, spot } : null;
    },
    write: (v, dateKey) => {
      if (v?.rowsBySym && Object.keys(v.rowsBySym).length) {
        useOptionsStore.setState({
          rowsBySym: v.rowsBySym,
          spot: v.spot ?? null,
          rowsTradingDate: dateKey,
        });
      }
    },
  },
  {
    key: 'indexBars', // WebSocketDataStore.indexData.SPX (formatted bars) → chart price
    read: () => {
      const bars = useWebSocketDataStore.getState().indexData?.SPX;
      if (!Array.isArray(bars) || !bars.length) return null;
      // Same rule as chainRows: bars still carrying an earlier session are a
      // restore, and re-persisting them under today's key would make a reload
      // at the next open present them as the current session.
      const last = bars[bars.length - 1];
      if (!Number.isFinite(last?.time) || etDateKey(last.time) !== etDateKey(Date.now())) {
        return null;
      }
      return { SPX: bars };
    },
    write: (v) => {
      // Restore directly (bars are already {time,open,…}); do NOT route through
      // setIndexData, which strips a prior day via its current-trading-day filter.
      if (Array.isArray(v?.SPX) && v.SPX.length) {
        useWebSocketDataStore.setState((s) => ({ indexData: { ...s.indexData, SPX: v.SPX } }));
      }
    },
  },
];

// Hydrate every registered slice from its newest stored day. Call once on app mount.
export function hydrateDailyCache() {
  for (const s of DAILY_CACHE_SERIES) {
    const dateKey = newestDailyDateKey(s.key);
    const v = hydrateNewestDaily(s.key);
    if (v != null) {
      try { s.write(v, dateKey); } catch { /* one bad entry must not break the others / boot */ }
    }
  }
}

// Persist every registered slice. Call on the existing once-per-minute capture tick.
export function persistDailyCache() {
  for (const s of DAILY_CACHE_SERIES) {
    let v = null;
    try { v = s.read(); } catch { v = null; }
    if (v != null) persistDaily(s.key, v);
  }
}
